import { useEffect } from 'react';
import { useSnapshot } from 'valtio';

import { RotationOffsets, Variation } from '@cartwhl/common/types';
import { TimelineItem } from '@cartwhl/ui/feature/Timeline';

import { AnimationMiniPlayer, MiniControls } from 'components/AnimationPlayer';
import { animationPlayerStore, projectStore, sceneSettingsStore } from 'stores';
import getCharacterDataFromProject from 'utils/getCharacterDataFromProject';

import CurrenMotionFeedback from './CurrenMotionFeedback';

export default function CurrentMotion({
  currentVariation,
  currentTimelineItem,
  onTrimChange,
  frameRate,
  variationNumber,
  onFavoriteToggle,
}: {
  currentVariation: Variation;
  currentTimelineItem: TimelineItem;
  onTrimChange?: (trimStart?: number, trimEnd?: number) => void;
  frameRate: number;
  variationNumber: number;
  onFavoriteToggle?: (animationNumber: string, favorite: boolean) => void;
}) {
  const { currentProject } = useSnapshot(projectStore.state);
  const { characterSettings } = useSnapshot(sceneSettingsStore.state);
  const playerStore =
    animationPlayerStore.getAnimationPlayerStoreByID('edit-motion');

  const { trimFrameStart, trimFrameEnd, duration } = useSnapshot(
    playerStore.state,
  );
  const characterData = getCharacterDataFromProject(currentProject?.data);

  useEffect(() => {
    // Set initial values of playerStore
    if (
      currentTimelineItem.trimStart === undefined ||
      currentTimelineItem.trimEnd === undefined
    )
      return;
    playerStore.setTrimFrames([
      currentTimelineItem.trimStart
        ? currentTimelineItem.trimStart * frameRate
        : 0,
      currentTimelineItem.trimEnd
        ? currentTimelineItem.trimEnd * frameRate
        : duration.originalFrames,
    ]);
  }, [
    currentTimelineItem.trimStart,
    currentTimelineItem.trimEnd,
    duration.originalFrames,
  ]);

  useEffect(() => {
    onTrimChange?.(trimFrameStart, trimFrameEnd);
  }, [trimFrameStart, trimFrameEnd]);

  if (!characterData?.meshPath) return null;

  return (
    <div className="relative h-[414px] w-full rounded-2xl">
      <AnimationMiniPlayer
        playerName="Current motion"
        characterID={characterData?.characterID}
        meshPath={characterData?.meshPath}
        rotationOffsets={characterData?.rotationOffsets as RotationOffsets}
        animationPath={currentVariation.bvhURL}
        animationPlayerStore={playerStore}
        defaultCameraPosition="close"
        characterSettings={characterSettings}
        showCameraButtons
        cameraFollow={true}
        frameRate={frameRate}
        miniControls={
          <div className="flex flex-col gap-2">
            <CurrenMotionFeedback
              className="self-end"
              variation={currentVariation}
              jobID={currentTimelineItem.jobID}
              variationNumber={variationNumber}
              onFavoriteToggle={onFavoriteToggle}
            />
            <MiniControls.Root animationPlayerStore={playerStore}>
              <MiniControls.PlayButton />
              {/* <MiniControls.FrameCount /> */}
              <MiniControls.TrimBar
                frameRate={frameRate}
                minFrames={Math.floor(frameRate * 1.2)}
              />
            </MiniControls.Root>
          </div>
        }
      />
    </div>
  );
}
